import * as Sentry from "@sentry/react";
import { EventEmitter } from "eventemitter3";
import type {
  ClientMessage,
  ServerMessage,
  ServerAnswerRevealed,
  ServerQuestion,
  ServerGoAway,
  ServerError,
} from "../../shared/live-proxy-protocol";

// ── Connection status ─────────────────────────────────────────────────────────

export type ConnectionStatus = "idle" | "connecting" | "connected" | "reconnecting" | "disconnected";

// ── Event types ───────────────────────────────────────────────────────────────

export interface LiveProxyEventTypes {
  open:            () => void;
  close:           (event: CloseEvent) => void;
  ready:           () => void;
  question:        (data: ServerQuestion) => void;
  setup_complete:  () => void;
  audio:           (data: ArrayBuffer) => void;
  turn_complete:   () => void;
  interrupted:     () => void;
  answer_revealed: (data: ServerAnswerRevealed) => void;
  go_away:         (data: ServerGoAway) => void;
  error:           (data: ServerError) => void;
  status:          (status: ConnectionStatus) => void;
}

// ── Config ────────────────────────────────────────────────────────────────────

const RECONNECT_BASE_MS      = 1_000;
const RECONNECT_MAX_MS       = 30_000;
const MAX_RECONNECT_ATTEMPTS = 3;
const HEARTBEAT_INTERVAL_MS  = 25_000;
const HEARTBEAT_TIMEOUT_MS   = 5_000;
const MAX_QUEUED_MESSAGES    = 50;

// ── Client ────────────────────────────────────────────────────────────────────

export class LiveProxyClient extends EventEmitter<LiveProxyEventTypes> {
  private ws: globalThis.WebSocket | null = null;
  private _status: ConnectionStatus = "idle";
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;
  private pendingQueue: ClientMessage[] = [];

  get status(): ConnectionStatus { return this._status; }
  get connected(): boolean       { return this._status === "connected"; }

  // ── Public API ──

  connect(): void {
    // Reset destroyed so explicit reconnects (e.g. React StrictMode double-invoke) always work
    this.destroyed = false;
    if (this.ws && this.ws.readyState <= globalThis.WebSocket.OPEN) return;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    this.reconnectAttempt = 0;
    this._setStatus("connecting");
    this._openSocket();
  }

  disconnect(): void {
    this.destroyed = true;
    this.pendingQueue = [];
    this._stopHeartbeat();
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }

    if (this.ws) {
      const ws = this.ws;
      this.ws = null; // nullify first so stale-socket guards fire in all handlers
      if (ws.readyState === globalThis.WebSocket.CONNECTING) {
        // Defer close until the handshake completes to avoid EPIPE through Vite's WS proxy
        ws.addEventListener("open", () => ws.close(1000, "disposed"), { once: true });
      } else {
        ws.close();
      }
    }

    // Intentional teardown is idle, never a connection failure.
    this._setStatus("idle");
  }

  startQuestion(personaProfileId: string, previousOutcome: "correct" | "incorrect" | null): void {
    if (this._status === "idle" || this._status === "disconnected") this.connect();
    this._send({ type: "start_question", personaProfileId, previousOutcome });
  }

  sendAudio(data: string, mimeType: string): void {
    this._send({ type: "audio", data, mimeType });
  }

  sendActivityStart(): void { this._send({ type: "activity_start" }); }
  sendActivityEnd():   void { this._send({ type: "activity_end" }); }

  sendChip(chip: "hint" | "repeat"): void {
    this._send({ type: "chip", chip });
  }

  sendAnswerChoice(text: string): void {
    this._send({ type: "answer_choice", text });
  }

  // ── Private: socket lifecycle ──

  private _openSocket(): void {
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new globalThis.WebSocket(`${protocol}//${location.host}/api/live-proxy`);
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    ws.onopen = () => {
      if (ws !== this.ws) return; // stale socket guard
      this.reconnectAttempt = 0;
      this._setStatus("connected");
      this._startHeartbeat();
      this._flushQueue();
      this.emit("open");
    };

    ws.onclose = (e) => {
      if (ws !== this.ws) return; // stale socket guard
      this._stopHeartbeat();
      Sentry.addBreadcrumb({ category: "websocket", message: "WebSocket closed", level: e.wasClean ? "info" : "warning", data: { code: e.code, reason: e.reason || undefined, attempt: this.reconnectAttempt } });
      this.emit("close", e);
      if (!this.destroyed) this._scheduleReconnect();
    };

    ws.onmessage = (event) => {
      if (ws !== this.ws) return; // stale socket guard
      let msg: ServerMessage;
      try { msg = JSON.parse(event.data as string); }
      catch (err) {
        Sentry.captureException(err, { extra: { rawLength: (event.data as string)?.length } });
        return;
      }

      // Heartbeat reply — consume without emitting
      if (msg.type === "pong") {
        if (this.pongTimer) { clearTimeout(this.pongTimer); this.pongTimer = null; }
        return;
      }

      switch (msg.type) {
        case "ready":           this.emit("ready"); break;
        case "question":        this.emit("question", msg); break;
        case "setup_complete":  this.emit("setup_complete"); break;
        case "audio":           this.emit("audio", base64ToArrayBuffer(msg.data)); break;
        case "turn_complete":   this.emit("turn_complete"); break;
        case "interrupted":     this.emit("interrupted"); break;
        case "answer_revealed": this.emit("answer_revealed", msg); break;
        case "go_away":         this.emit("go_away", msg); break;
        case "error":           this.emit("error", msg); break;
      }
    };
  }

  // ── Private: reconnect ──

  private _scheduleReconnect(): void {
    if (this.reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      Sentry.addBreadcrumb({ category: "websocket", message: "Max reconnect attempts reached — giving up", level: "error", data: { attempts: this.reconnectAttempt } });
      this.pendingQueue = [];
      this._setStatus("disconnected");
      return;
    }

    this._setStatus("reconnecting");
    this.reconnectAttempt++;

    // Exponential backoff with ±50% jitter
    const base  = Math.min(RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempt - 1), RECONNECT_MAX_MS);
    const delay = base * (0.5 + Math.random() * 0.5);
    Sentry.addBreadcrumb({ category: "websocket", message: "Scheduling reconnect", level: "info", data: { attempt: this.reconnectAttempt, delayMs: Math.round(delay) } });

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.destroyed) this._openSocket();
    }, delay);
  }

  // ── Private: heartbeat ──

  private _startHeartbeat(): void {
    this._stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      // Skip ping when tab is backgrounded (iOS AudioContext + stale WS mitigation)
      if (document.visibilityState !== "visible") return;
      if (this.ws?.readyState !== globalThis.WebSocket.OPEN) return;

      this.ws.send(JSON.stringify({ type: "ping" } satisfies ClientMessage));

      this.pongTimer = setTimeout(() => {
        this.pongTimer = null;
        Sentry.addBreadcrumb({ category: "websocket", message: "Heartbeat timeout — forcing reconnect", level: "warning" });
        // No pong — connection is stale; force close to trigger reconnect
        this.ws?.close(4001, "heartbeat timeout");
      }, HEARTBEAT_TIMEOUT_MS);
    }, HEARTBEAT_INTERVAL_MS);
  }

  private _stopHeartbeat(): void {
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
    if (this.pongTimer)      { clearTimeout(this.pongTimer);      this.pongTimer = null; }
  }

  // ── Private: send ──

  private _setStatus(status: ConnectionStatus): void {
    if (this._status === status) return;
    this._status = status;
    this.emit("status", status);
  }

  private _send(msg: ClientMessage): void {
    if (this.ws?.readyState === globalThis.WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
      return;
    }
    if (this.destroyed) return;

    // Socket still connecting or reconnecting — queue and flush on open.
    // Dropping here left mobile users stuck on the loading state forever.
    if (this.pendingQueue.length >= MAX_QUEUED_MESSAGES) this.pendingQueue.shift();
    this.pendingQueue.push(msg);
    Sentry.addBreadcrumb({ category: "websocket", message: "Queued message while socket not open", level: "info", data: { type: msg.type, queueLength: this.pendingQueue.length } });
  }

  private _flushQueue(): void {
    if (!this.pendingQueue.length) return;
    if (this.ws?.readyState !== globalThis.WebSocket.OPEN) return;
    const queued = this.pendingQueue;
    this.pendingQueue = [];
    for (const msg of queued) this.ws.send(JSON.stringify(msg));
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0)).buffer;
}
